import { useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { getImportTaskStatus } from './getImportTaskStatus'
import { TaskModel } from '@/app/models/tasks/taskModel'
import { TaskStatus } from '@/app/constants/taskStatus'
import { getTaskStatus } from '@/app/services/task/taskStatusResolver'

type Props = {
  companyId: string
  importTaskId: string

  fileId: string
  taskId: string
  disabled?: boolean
  onDone?: (task: TaskModel) => void
  intervalMs?: number
}

const QUERY_KEY_IMPORT_TASK_STATUS = 'QUERY_KEY_IMPORT_TASK_STATUS'

const isStillProcessing = (task: TaskModel) => {
  const taskStatus: TaskStatus = getTaskStatus(task.status)
  switch (taskStatus) {
    case TaskStatus.Importing:
    case TaskStatus.Pending:
    case TaskStatus.New:
      return true
    case TaskStatus.Completed:
    case TaskStatus.Error:
    default:
      return false
  }
}

/**
 * Fetches available countries.
 */
export function useImportStatusPooling(props: Props) {
  const {
    companyId,
    importTaskId,
    fileId,
    taskId,
    disabled,
    onDone,
    intervalMs = 5000,
  } = props

  return useQuery<TaskModel>({
    queryKey: [QUERY_KEY_IMPORT_TASK_STATUS, companyId, fileId, taskId, importTaskId],
    queryFn: async () => {
      const { data, status } = await getImportTaskStatus(props)
      if (status !== 200) {
        return null
      }
      if(onDone && !isStillProcessing(data)){
        onDone(data)
      }
      return data
    },
    staleTime: MINUTE_IN_MS * 60, // cache for 60 minutes
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || disabled || !isStillProcessing(data)) {
        return false
      }
      return intervalMs
    },
    enabled:
      !disabled &&
      Boolean(importTaskId) &&
      Boolean(companyId) &&
      Boolean(fileId) &&
      Boolean(taskId),
  })
}
